import { useState } from "react"
import { useNavigate } from "react-router"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { useServerTable } from "../../hooks/useServerTable"
import { GroupIcon, PencilIcon, TrashBinIcon } from "../../icons"

// ── Types ──────────────────────────────────────────────────────────────────

type InstitutionType = "educational" | "government" | "private_company" | "ngo" | "other"

const INSTITUTION_TYPE_LABELS: Record<InstitutionType, string> = {
  educational: "Institución educativa",
  government: "Entidad gubernamental",
  private_company: "Empresa privada",
  ngo: "ONG",
  other: "Otra",
}

type Institution = {
  id: string
  name: string
  slug: string
  description?: string | null
  type?: InstitutionType
  contactName?: string | null
  contactEmail?: string | null
  seedbeds_count?: number
  createdAt?: string
}

// ── Component ──────────────────────────────────────────────────────────────

export default function InstitutionsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState<"" | InstitutionType>("")
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [institutionToDelete, setInstitutionToDelete] = useState<Institution | null>(null)  const {
    rows: institutions,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    reload,
  } = useServerTable<Institution>({
    endpoint: "/academy/seedbeds/admin/institutions",
    defaultPageSize: 20,
    defaultSort: { column: "createdAt", dir: "desc" },
    // El tipo viaja al servidor: el total y la paginación son reales con cualquier cantidad de organizaciones.
    params: { search, type: typeFilter || undefined },
    extractRows: (data) => data.institutions ?? [],
    extractTotal: (data) => data.pagination?.total ?? 0,
  })

  // Metrics
  const withSeedbeds = institutions.filter((i) => (i.seedbeds_count ?? 0) > 0).length
  const withoutSeedbeds = institutions.length - withSeedbeds

  // Delete
  async function onDelete(id: string) {
    setDeletingId(id)
    try {
      await api.delete(`/academy/seedbeds/admin/institutions/${id}`)
      toastSuccess("Organización eliminada")
      await reload()
    } catch (err) {
      toastError(err)
    } finally {
      setDeletingId(null)
      setInstitutionToDelete(null)
    }
  }

  const columns: DataTableColumn<Institution>[] = [
    {
      key: "name",
      label: "Organización",
      sortable: true,
      render: (inst) => (
        <>
          <p className="font-semibold text-gray-900 dark:text-white">{inst.name}</p>
          {inst.description && (
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{inst.description}</p>
          )}
        </>
      ),
    },
    {
      key: "type",
      label: "Tipo",
      render: (inst) => (
        <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-semibold text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
          {INSTITUTION_TYPE_LABELS[inst.type ?? "other"]}
        </span>
      ),
    },
    {
      key: "slug",
      label: "Slug",
      sortable: true,
      render: (inst) => (
        <code className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
          {inst.slug}
        </code>
      ),
    },
    {
      key: "contact",
      label: "Contacto",
      render: (inst) =>
        inst.contactName || inst.contactEmail ? (
          <>
            {inst.contactName && <p className="text-gray-700 dark:text-gray-300">{inst.contactName}</p>}
            {inst.contactEmail && <p className="text-xs text-gray-500">{inst.contactEmail}</p>}
          </>
        ) : (
          <span className="text-xs text-gray-500 dark:text-gray-400">—</span>
        ),
    },
    {
      key: "seedbeds_count",
      label: "Semilleros",
      align: "center",
      render: (inst) => (
        <span
          className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            (inst.seedbeds_count ?? 0) > 0
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
              : "bg-gray-100 text-gray-500 dark:bg-gray-800"
          }`}
        >
          {inst.seedbeds_count ?? 0}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Acciones",
      align: "right",
      render: (inst) => (
        <RowActionsMenu
          actions={[
            {
              label: "Ver semilleros",
              icon: <GroupIcon />,
              to: `/programs/seedbeds?institution_id=${inst.id}`,
            },
            {
              label: "Editar",
              icon: <PencilIcon />,
              to: `/programs/institutions/${inst.id}/edit`,
              requires: "academy:institutions:edit",
            },
            {
              label: deletingId === inst.id ? "Eliminando..." : "Eliminar",
              icon: <TrashBinIcon />,
              onClick: () => setInstitutionToDelete(inst),
              variant: "danger",
              disabled: deletingId === inst.id,
              requires: "academy:institutions:edit",
            },
          ]}
        />
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Programas", "Organizaciones"]}
      title="Organizaciones"
      subtitle="Clientes que contratan semilleros de estudio"
      tabs={[{ label: "Organizaciones", to: "/programs/institutions", active: true }]}
      metrics={[
        { label: "Total", value: String(total) },
        {
          label: "Con semilleros (esta página)",
          value: String(withSeedbeds),
          tone: "good",
          hint: "Ya tienen actividad activa — solo cuenta las filas visibles, no el total",
        },
        {
          label: "Sin semilleros (esta página)",
          value: String(withoutSeedbeds),
          tone: withoutSeedbeds > 0 ? "warning" : "neutral",
          hint: "Organización registrada sin ningún semillero abierto — solo cuenta las filas visibles, no el total",
        },
      ]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar organización...",
        },
        filters: [
          {
            type: "select",
            key: "type",
            label: "Tipo",
            value: typeFilter,
            onChange: (v) => setTypeFilter(v as "" | InstitutionType),
            options: [
              { label: "Todos", value: "" },
              ...(Object.entries(INSTITUTION_TYPE_LABELS) as [InstitutionType, string][]).map(([value, label]) => ({
                label,
                value,
              })),
            ],
          },
        ],
        onRefresh: reload,
        refreshing: loading,        onCreate: () => navigate("/programs/institutions/new"),
        createLabel: "+ Nueva organización",
        requires: "academy:institutions:edit",
      }}
    >
      <DataTable
        columns={columns}
        rows={institutions}
        rowKey={(inst) => inst.id}
        loading={loading}
        emptyMessage="No se encontraron organizaciones."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={total}
        onPageChange={setPage}
      />      <ConfirmDialog
        isOpen={institutionToDelete !== null}
        title={`¿Eliminar "${institutionToDelete?.name ?? ""}"?`}
        message="Solo es posible si no tiene semilleros asociados."
        confirmLabel="Eliminar"
        confirming={deletingId === institutionToDelete?.id}
        onConfirm={() => institutionToDelete && onDelete(institutionToDelete.id)}
        onCancel={() => setInstitutionToDelete(null)}
      />
    </ModuleLayout>
  )
}
